import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../../../services/api';
import './BatchRuleBuilder.css';

/**
 * BatchRuleBuilder - Spreadsheet-style interface for creating many gates at once.
 *
 * Each row represents one rule: Input Nodes -> Gate Type -> Output Nodes.
 * Users can add/remove rows and submit all at once via the batch endpoint.
 */

/* ------------------------------------------------------------------ */
/*  InlinChipSearch - compact chip search for use inside table cells   */
/* ------------------------------------------------------------------ */
function InlineChipSearch({ graphId, selectedNodes, onAdd, onRemove, placeholder }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2 || !graphId) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const resp = await apiService.listNodes({ graphId, query: q, pageSize: 15 });
      if (resp.success) {
        setResults(resp.nodes || []);
      }
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [graphId]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  const selectedIds = new Set(selectedNodes.map(n => n.id));
  const filtered = results.filter(n => !selectedIds.has(n.id || n.nodeId));

  return (
    <div className="brb-chip-cell" ref={containerRef}>
      <div className="brb-chips-wrap">
        {selectedNodes.map((node) => (
          <span key={node.id} className="brb-chip">
            <span className="brb-chip-name">{node.name}</span>
            <button
              type="button"
              className="brb-chip-remove"
              onClick={() => onRemove(node.id)}
            >
              &times;
            </button>
          </span>
        ))}
        <input
          type="text"
          className="brb-chip-input"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
          placeholder={selectedNodes.length === 0 ? (placeholder || 'Search...') : '+'}
          size={selectedNodes.length === 0 ? 12 : 3}
        />
      </div>
      {showDropdown && query.length >= 2 && (
        <div className="brb-dropdown">
          {loading && <div className="brb-dropdown-msg">Searching...</div>}
          {!loading && filtered.length === 0 && (
            <div className="brb-dropdown-msg">No nodes found</div>
          )}
          {filtered.map((node) => {
            const nId = node.id || node.nodeId;
            const nName = node.name || node.nodeName;
            const nType = node.type || node.nodeType || '';
            // Check if search matched via an alias
            const nodeAliases = node.aliases || [];
            const q = query.toLowerCase();
            const matchingAlias = nodeAliases.find(a =>
              a.toLowerCase().includes(q) && a.toLowerCase() !== (nName || '').toLowerCase()
            );
            return (
              <div
                key={nId}
                className="brb-dropdown-item"
                onClick={() => {
                  onAdd({ id: nId, name: nName, type: nType });
                  setQuery('');
                  setResults([]);
                }}
              >
                <span className="brb-dropdown-item-name">
                  {nName}
                  {matchingAlias && (
                    <span style={{ color: '#888', fontSize: '0.85em', marginLeft: 4 }}>
                      (aka: {matchingAlias})
                    </span>
                  )}
                </span>
                {nType && <span className="brb-dropdown-item-type">{nType}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Helper: create a blank row                                         */
/* ------------------------------------------------------------------ */
let rowIdCounter = 0;
function blankRow() {
  return {
    _id: ++rowIdCounter,
    inputNodes: [],
    gateType: 'AND',
    outputNodes: [],
    name: '',
    status: 'pending', // pending | creating | success | error
    errorMsg: '',
  };
}

/* ------------------------------------------------------------------ */
/*  BatchRuleBuilder main component                                    */
/* ------------------------------------------------------------------ */
function BatchRuleBuilder({ isOpen, onClose, onGatesCreated, graphId }) {
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      rowIdCounter = 0;
      setRows([blankRow(), blankRow(), blankRow()]);
      setSubmitting(false);
      setProgress({ done: 0, total: 0 });
    }
  }, [isOpen]);

  const addRow = () => {
    setRows(prev => [...prev, blankRow()]);
  };

  const removeRow = (rowId) => {
    setRows(prev => prev.filter(r => r._id !== rowId));
  };

  const updateRow = (rowId, field, value) => {
    setRows(prev => prev.map(r => r._id === rowId ? { ...r, [field]: value } : r));
  };

  const addNodeToRow = (rowId, field, node) => {
    setRows(prev => prev.map(r => {
      if (r._id !== rowId) return r;
      const existing = r[field];
      if (existing.find(n => n.id === node.id)) return r;
      return { ...r, [field]: [...existing, node] };
    }));
  };

  const removeNodeFromRow = (rowId, field, nodeId) => {
    setRows(prev => prev.map(r => {
      if (r._id !== rowId) return r;
      return { ...r, [field]: r[field].filter(n => n.id !== nodeId) };
    }));
  };

  // Auto-generate name for a row
  const autoName = (row) => {
    if (row.name) return row.name;
    const inputPart = row.inputNodes.map(n => n.name).join(` ${row.gateType} `);
    if (row.outputNodes.length > 0) {
      return `${inputPart} -> ${row.outputNodes.map(n => n.name).join(', ')}`;
    }
    return inputPart || '';
  };

  // Validate rows that are ready to submit
  const validRows = rows.filter(
    r => r.inputNodes.length >= 2 && r.outputNodes.length >= 1 && r.status !== 'success'
  );

  const handleSubmitAll = async () => {
    if (validRows.length === 0) return;

    setSubmitting(true);
    setProgress({ done: 0, total: validRows.length });

    // Mark all valid rows as "creating"
    setRows(prev => prev.map(r => {
      if (validRows.find(v => v._id === r._id)) {
        return { ...r, status: 'creating', errorMsg: '' };
      }
      return r;
    }));

    // Try batch endpoint first, fall back to individual calls
    const rules = validRows.map(r => ({
      gateType: r.gateType,
      name: autoName(r) || `${r.gateType} Gate`,
      description: `${r.gateType} gate: ${r.inputNodes.map(n => n.name).join(' + ')} -> ${r.outputNodes.map(n => n.name).join(', ')}`,
      inputs: r.inputNodes.map(n => n.id),
      outputs: r.outputNodes.map(n => n.id),
    }));

    try {
      const batchResp = await apiService.batchCreateGates({ graphId, rules });

      if (batchResp.success && batchResp.results) {
        // Map results back to rows
        let doneCount = 0;
        setRows(prev => prev.map(r => {
          const idx = validRows.findIndex(v => v._id === r._id);
          if (idx === -1) return r;
          const result = batchResp.results[idx];
          if (result && result.success) {
            doneCount++;
            return { ...r, status: 'success', errorMsg: '' };
          } else {
            return { ...r, status: 'error', errorMsg: result?.message || 'Unknown error' };
          }
        }));
        setProgress({ done: doneCount, total: validRows.length });
      } else {
        throw new Error('Batch endpoint failed');
      }
    } catch {
      // Fall back to individual createGate calls
      let doneCount = 0;
      for (let i = 0; i < validRows.length; i++) {
        const row = validRows[i];
        const rule = rules[i];
        try {
          const resp = await apiService.createGate({
            graphId,
            gateType: rule.gateType,
            name: rule.name,
            description: rule.description,
            inputs: rule.inputs,
            outputs: rule.outputs,
          });
          if (resp.success) {
            doneCount++;
            setRows(prev => prev.map(r =>
              r._id === row._id ? { ...r, status: 'success', errorMsg: '' } : r
            ));
          } else {
            setRows(prev => prev.map(r =>
              r._id === row._id ? { ...r, status: 'error', errorMsg: resp.message || 'Failed' } : r
            ));
          }
        } catch (err) {
          setRows(prev => prev.map(r =>
            r._id === row._id ? { ...r, status: 'error', errorMsg: err.message || 'Failed' } : r
          ));
        }
        setProgress({ done: doneCount, total: validRows.length });
      }
    }

    setSubmitting(false);

    if (onGatesCreated) {
      onGatesCreated();
    }
  };

  const successCount = rows.filter(r => r.status === 'success').length;
  const errorCount = rows.filter(r => r.status === 'error').length;
  const pendingValidCount = validRows.length;

  if (!isOpen) return null;

  return (
    <div className="brb-overlay" onClick={onClose}>
      <div className="brb-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="brb-header">
          <div>
            <p className="brb-eyebrow">Batch Mode</p>
            <h2>Create Multiple Rules</h2>
          </div>
          <div className="brb-header-right">
            {successCount > 0 && (
              <span className="brb-success-badge">{successCount} created</span>
            )}
            {errorCount > 0 && (
              <span className="brb-error-badge">{errorCount} failed</span>
            )}
            <button className="brb-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        {/* Body */}
        <div className="brb-body">
          {submitting && (
            <div className="brb-progress">
              <div className="brb-progress-bar">
                <div
                  className="brb-progress-fill"
                  style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
                />
              </div>
              <span className="brb-progress-text">
                {progress.done} / {progress.total} rules created
              </span>
            </div>
          )}

          <div className="brb-table-wrap">
            <table className="brb-table">
              <thead>
                <tr>
                  <th className="brb-th-num">#</th>
                  <th className="brb-th-nodes">Input Nodes (2+)</th>
                  <th className="brb-th-gate">Gate</th>
                  <th className="brb-th-nodes">Output Nodes (1+)</th>
                  <th className="brb-th-name">Name</th>
                  <th className="brb-th-status">Status</th>
                  <th className="brb-th-actions"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr
                    key={row._id}
                    className={`brb-row brb-row--${row.status}`}
                  >
                    <td className="brb-td-num">{idx + 1}</td>
                    <td className="brb-td-nodes">
                      <InlineChipSearch
                        graphId={graphId}
                        selectedNodes={row.inputNodes}
                        onAdd={(node) => addNodeToRow(row._id, 'inputNodes', node)}
                        onRemove={(nodeId) => removeNodeFromRow(row._id, 'inputNodes', nodeId)}
                        placeholder="Search inputs..."
                      />
                    </td>
                    <td className="brb-td-gate">
                      <select
                        className="brb-gate-select"
                        value={row.gateType}
                        onChange={(e) => updateRow(row._id, 'gateType', e.target.value)}
                        disabled={row.status === 'success'}
                      >
                        <option value="AND">AND</option>
                        <option value="OR">OR</option>
                      </select>
                    </td>
                    <td className="brb-td-nodes">
                      <InlineChipSearch
                        graphId={graphId}
                        selectedNodes={row.outputNodes}
                        onAdd={(node) => addNodeToRow(row._id, 'outputNodes', node)}
                        onRemove={(nodeId) => removeNodeFromRow(row._id, 'outputNodes', nodeId)}
                        placeholder="Search outputs..."
                      />
                    </td>
                    <td className="brb-td-name">
                      <input
                        type="text"
                        className="brb-name-input"
                        value={row.name}
                        onChange={(e) => updateRow(row._id, 'name', e.target.value)}
                        placeholder={autoName(row) || 'Auto-generated'}
                        disabled={row.status === 'success'}
                      />
                    </td>
                    <td className="brb-td-status">
                      {row.status === 'pending' && <span className="brb-status-dot brb-status-dot--pending" title="Ready" />}
                      {row.status === 'creating' && <span className="brb-status-dot brb-status-dot--creating" title="Creating..." />}
                      {row.status === 'success' && <span className="brb-status-dot brb-status-dot--success" title="Created" />}
                      {row.status === 'error' && (
                        <span className="brb-status-dot brb-status-dot--error" title={row.errorMsg || 'Error'} />
                      )}
                    </td>
                    <td className="brb-td-actions">
                      {row.status !== 'success' && rows.length > 1 && (
                        <button
                          type="button"
                          className="brb-remove-row"
                          onClick={() => removeRow(row._id)}
                          title="Remove row"
                          disabled={submitting}
                        >
                          &times;
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            className="brb-add-row"
            onClick={addRow}
            disabled={submitting}
          >
            + Add Row
          </button>
        </div>

        {/* Footer */}
        <div className="brb-footer">
          <div className="brb-footer-info">
            {pendingValidCount > 0 && (
              <span>{pendingValidCount} rule{pendingValidCount !== 1 ? 's' : ''} ready to create</span>
            )}
          </div>
          <div className="brb-footer-actions">
            <button
              type="button"
              className="brb-btn brb-btn--secondary"
              onClick={onClose}
              disabled={submitting}
            >
              {successCount > 0 ? 'Done' : 'Cancel'}
            </button>
            <button
              type="button"
              className="brb-btn brb-btn--primary"
              onClick={handleSubmitAll}
              disabled={submitting || pendingValidCount === 0}
            >
              {submitting
                ? `Creating ${progress.done}/${progress.total}...`
                : `Create All (${pendingValidCount})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BatchRuleBuilder;
